import { FC, Fragment, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { BKJSelect, BKJSelectSearch, IBKJThemeColor } from '@awais4123/fillify-design-library';

import { QuerySwitch } from 'components/QuerySwitch';
import { ComponentMapType, SSFBehaviorsValueType } from 'types/common.types';
import { IBKJSelectProps } from '@awais4123/fillify-design-library/lib/components/BKJSelect/BKJSelect.types';
import { usePersistentState } from 'hooks';
import {
  ChangeEventType,
  ChangeFunctionType,
} from '@awais4123/fillify-design-library/lib/common.types';

import { ISSFBehaviorsQueryResult } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { SessionService } from 'services';
import { getSelectPositionByDepartmentOptions } from 'components/JaneModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.utils';

import {
  SelectInputPositionByDepartmentContext,
  SelectInputPositionByDepartmentProvider,
} from 'components/JaneModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.context';
import {
  GroupOption,
  NoResultsWrapper,
  PositionOption,
} from 'components/JaneModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.styled';
import {
  DropdownListItemVariant,
  SelectPositionByDepartmentDataType,
  SelectPositionByDepartmentRenderGroupProps,
} from 'components/JaneModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.types';
import { TeamMemberPageContext } from 'pages/TeamMemberPage/TeamMemberPage.context';

const RenderGroup = ({
  name = 'positions',
  type = 'Multiple',
  selected,
  onChange: _onChange,
  dropdownProps = {},
}: SelectPositionByDepartmentRenderGroupProps) => {
  const { t, i18n } = useTranslation();
  const {
    isSearchable = true,
    dropdownListItemVariant = 'Checkbox',
  }: { isSearchable?: boolean; dropdownListItemVariant?: DropdownListItemVariant } = dropdownProps;
  const {
    values,
    onChange,
  }: {
    values: SSFBehaviorsValueType;
    onChange: ChangeFunctionType;
    data: SelectPositionByDepartmentDataType;
    positionSelectInputOnClose: () => void;
  } = useContext(SelectInputPositionByDepartmentContext);
  const selectedPositionIds = selected.map((value) => Number(value));

  const onSelectOption = (e: ChangeEventType) => {
    const { name, value }: { name: string; value: number } = e.target;
    switch (type) {
      case 'Single':
        _onChange({
          target: {
            name,
            value: value,
          },
        });
        break;
      case 'Multiple':
        if (selectedPositionIds.includes(value)) {
          _onChange({
            target: {
              name,
              value: selectedPositionIds.filter((position) => position !== value),
            },
          });
        } else {
          _onChange({
            target: {
              name,
              value: selectedPositionIds.concat(value),
            },
          });
        }
        break;
      default:
        break;
    }
  };

  const dropdownHeaderComponentMap: ComponentMapType = {
    Single: () => null,
  };
  const DepartmentSelectComponent = ({ data }: { data: SelectPositionByDepartmentDataType }) => {
    if (data && Object.keys(data).length <= 0)
      return <NoResultsWrapper>{t('common:NO_RESULTS')}</NoResultsWrapper>;
    return (
      <Fragment>
        {Object.keys(data).map((group: string) => {
          return (
            <Fragment key={group}>
              <GroupOption
                key={group}
                id={group}
                value={group}
                label={i18n.exists(`team_member:${group}`) ? t(`team_member:${group}`) : group}
                name={group}
                onChange={() => {}}
                selected={[]}
                variant={dropdownListItemVariant}
                type={type}
              />
              {data[group].map(({ label, value, key }) => {
                return (
                  <PositionOption
                    key={key}
                    id={key}
                    data-cy={`SelectInputPositionByDepartmentOption-${label}`}
                    value={value}
                    label={
                      i18n.exists(`agency_dashboard:${label}`)
                        ? t(`agency_dashboard:${label}`)
                        : label
                    }
                    name={name}
                    onChange={onSelectOption}
                    selected={selected}
                    variant={dropdownListItemVariant}
                  />
                );
              })}
            </Fragment>
          );
        })}
      </Fragment>
    );
  };
  const DropdownHeaderComponent = dropdownHeaderComponentMap[type];
  return (
    <Fragment>
      <DropdownHeaderComponent />
      {isSearchable && (
        <BKJSelectSearch
          onChange={onChange}
          name="byName"
          value={values.byName}
          placeholder={t('common:SEARCH')}
        />
      )}
      <QuerySwitch
        context={SelectInputPositionByDepartmentContext}
        component={DepartmentSelectComponent}
      />
    </Fragment>
  );
};

const SelectInputPositionByDepartmentComponent: FC<
  {
    onChange: IBKJSelectProps['onChange'];
    onClose?: IBKJSelectProps['onClose'];
    value: IBKJSelectProps['value'];
    variant?: IBKJSelectProps['variant'];
    type?: IBKJSelectProps['type'];
  } & Omit<IBKJSelectProps, 'options' | 'renderOptions' | 'variant' | 'type'>
> = (props) => {
  const { t } = useTranslation();
  const {
    values: { location },
  } = useContext(TeamMemberPageContext);
  const {
    name = 'positions',
    placeholder = t('common:POSITION'),
    variant = 'GreyOutlinedPillClear',
    type = 'Multiple',
    width,
    error,
    dropdownVariant,
    dropdownProps,
    value,
    onChange,
    iconComponent,
    iconProps,
  } = props;
  const {
    data,
    onClose,
    onReset,
  }: {
    data: SelectPositionByDepartmentDataType;
    onReset: ISSFBehaviorsQueryResult['onReset'];
    onClose: () => void;
    values: Record<string, unknown>;
  } = useContext(SelectInputPositionByDepartmentContext);

  useEffect(() => {
    if (Object.keys(data).length > 0) {
      setOptions(data);
    }
  }, [data]); // eslint-disable-line
  const [options, setOptions] = usePersistentState(
    data,
    'SelectInputPositionByDepartmentComponent',
  );

  const _options = getSelectPositionByDepartmentOptions(options);

  const label = useRef<string | undefined>(undefined);

  if (Object.keys(data)?.length && value?.length) {
    const firstSelectedOption = _options.find((option) => option.value === value[0])?.label;
    if (firstSelectedOption) {
      label.current = `${firstSelectedOption}${
        value.length > 1 ? `... +${value.length - 1} ${t('MORE')}` : ''
      }`;
    }
  }

  let placeholderColor: keyof IBKJThemeColor | undefined;
  if (!label.current && variant === 'GreyOutlined') {
    placeholderColor = 'TextDisabled';
  }

  useEffect(() => {
    // this is because agency users have different positions for each location
    // check for location since its an optional prop and resetting on location change is not desired behavior in those cases
    if (!!location && SessionService.assertUserType('Agency')) {
      onReset && onReset();
    }
  }, [location]); // eslint-disable-line

  if (!Object.keys(options).length) return null;
  return (
    <BKJSelect
      options={[]}
      placeholder={placeholder}
      type={type}
      label={label.current}
      variant={variant}
      width={width}
      name={name}
      error={error}
      dropdownWidth="485px"
      dropdownVariant={dropdownVariant}
      dropdownProps={dropdownProps}
      dropdownPlacement="bottom-start"
      value={value}
      onChange={onChange}
      onClose={onClose}
      renderOptions={RenderGroup}
      iconComponent={iconComponent}
      iconProps={iconProps}
      placeholderColor={placeholderColor}
    />
  );
};

export const SelectInputPositionByDepartment: FC<
  {
    onChange: IBKJSelectProps['onChange'];
    value: IBKJSelectProps['value'];
    variant?: IBKJSelectProps['variant'];
    type?: IBKJSelectProps['type'];
    allocatedToCommunities?: string[];
  } & Omit<IBKJSelectProps, 'options' | 'renderOptions' | 'variant' | 'type'>
> = ({ allocatedToCommunities, ...props }) => {
  return (
    <SelectInputPositionByDepartmentProvider allocatedToCommunities={allocatedToCommunities}>
      <SelectInputPositionByDepartmentComponent {...props} />
    </SelectInputPositionByDepartmentProvider>
  );
};
